// HM-347 cluster 16.1.5: Step 3 pagamento pixel-aware mockup v3.
//
// - Anonymous-banner se isAnonymous (info Day Use senza dati ospite).
// - 2 card grandi 2 colonne (Carta / Contanti) selezione visiva.
// - Total card gradient con prezzo grande + breakdown da dynamicPricing
//   (cluster 16.1) o fallback modeBasePrice legacy.
// - Pulsante "Conferma e paga" vive nel WizardFooter (HM-349); qui
//   pulsante hidden compat data-testid="walkin-next-3" per test.

import { Banknote, Clock, CreditCard } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { type WalkInPayment, modeBasePrice, useWalkInWizard } from '../../stores/walkIn.js';

export function StepPayment() {
  const mode = useWalkInWizard((s) => s.mode);
  const payment = useWalkInWizard((s) => s.payment);
  const setPayment = useWalkInWizard((s) => s.setPayment);
  const setStep = useWalkInWizard((s) => s.setStep);
  const isAnonymous = useWalkInWizard((s) => s.isAnonymous);
  const anonymousId = useWalkInWizard((s) => s.anonymousId);
  const dynamicPricing = useWalkInWizard((s) => s.dynamicPricing);

  // priceCalc dinamico (cluster 16.1) ha precedenza sul fallback statico.
  const total = dynamicPricing.priceCalc?.total ?? modeBasePrice(mode);
  const breakdown = dynamicPricing.priceCalc?.breakdown ?? null;

  return (
    <section data-testid="step-payment" className="flex flex-col gap-6">
      {isAnonymous && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-[#FCF1EA]"
          style={{ borderColor: '#F2D4BC' }}
          data-testid="anonymous-banner"
        >
          <span className="w-9 h-9 rounded-lg bg-accent-soft flex items-center justify-center text-accent-primary text-base font-semibold">
            #
          </span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-primary">Modalita anonima · Day Use</p>
            <p className="text-xs text-text-secondary mt-0.5">
              Booking registrato come{' '}
              <span className="font-mono font-semibold text-accent-primary">{anonymousId}</span>
            </p>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-text-primary mb-3">Metodo di pagamento</h3>
        <div className="grid grid-cols-3 gap-4">
          <PaymentCard
            method="card"
            selected={payment === 'card'}
            onSelect={() => setPayment('card')}
            icon={<CreditCard size={28} />}
            label="Carta"
            sub="Revolut · POS contactless"
            testId="payment-card"
          />
          <PaymentCard
            method="cash"
            selected={payment === 'cash'}
            onSelect={() => setPayment('cash')}
            icon={<Banknote size={28} />}
            label="Contanti"
            sub="Glory CI-10 · resto auto"
            testId="payment-cash"
          />
          {/* Revisione pagamenti 2026-07-22: paga all'uscita — l'incasso e
              lo scontrino unificato avvengono al checkout. */}
          <PaymentCard
            method="later"
            selected={payment === 'later'}
            onSelect={() => setPayment('later')}
            icon={<Clock size={28} />}
            label="Paga all'uscita"
            sub="Incasso al check-out"
            testId="payment-later"
          />
        </div>
      </div>

      <div
        className="rounded-2xl p-6 text-text-on-accent"
        style={{
          background: 'linear-gradient(135deg, #E97B3D 0%, #D86A2C 100%)',
          boxShadow: '0 6px 24px rgba(233, 123, 61, 0.3)',
        }}
        data-testid="payment-total-card"
      >
        <p className="text-xs uppercase tracking-[0.5px] opacity-80">
          {payment === 'later' ? 'Da incassare al checkout' : 'Totale da pagare'}
        </p>
        <p className="text-4xl font-bold mt-1.5" data-testid="payment-total">
          EUR {total.toFixed(2)}
        </p>
        {breakdown && breakdown.length > 0 && (
          <ul
            className="mt-4 pt-4 border-t border-white/20 flex flex-col gap-1.5"
            data-testid="payment-breakdown"
          >
            {breakdown.map((b) => (
              <li
                key={`${b.label}-${b.amount}`}
                className="flex items-center justify-between text-sm"
              >
                <span className="opacity-90">{b.label}</span>
                <span className="font-semibold">EUR {b.amount.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Compat hidden button per test E2E pre-existenti */}
      <button
        type="button"
        data-testid="walkin-next-3"
        onClick={() => setStep(4)}
        className="hidden"
      >
        Conferma e paga
      </button>
    </section>
  );
}

// --------------------------- PaymentCard ---------------------------

function PaymentCard({
  method,
  selected,
  onSelect,
  icon,
  label,
  sub,
  testId,
}: {
  method: WalkInPayment;
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  label: string;
  sub: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testId}
      data-state={selected ? 'selected' : 'idle'}
      aria-pressed={selected}
      className={cn(
        'flex flex-col items-start gap-3 px-5 py-5 rounded-2xl border-2 text-left transition-all',
        selected
          ? 'border-accent-primary bg-accent-soft/30 shadow-[0_4px_16px_rgba(233,123,61,0.18)]'
          : 'border-border-input bg-bg-card hover:border-accent-primary/60 hover:bg-bg-base',
      )}
    >
      <span
        className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center transition-colors',
          selected ? 'bg-accent-primary text-text-on-accent' : 'bg-accent-soft text-accent-primary',
        )}
      >
        {icon}
      </span>
      <div>
        <p className="text-base font-semibold text-text-primary">{label}</p>
        <p className="text-xs text-text-secondary mt-0.5">{sub}</p>
      </div>
      <span className="sr-only">{method}</span>
    </button>
  );
}
